---
title: The agent surface
lede: The agent's own terminal, running in the sandbox, which you can leave and come back to.
order: 14
part: Run a task
---

The agent surface (`⌘1`) is the agent's real terminal — Claude Code or Codex — running inside the session's sandbox. Its tab in the surfaces column is named after the agent.

![The agent surface](media/agent-pane.webp)

The terminal lives in the sandbox, not in the window. Close the window or quit Airlock and the session is still where you left it. A session idle long enough to be parked wakes in seconds when you open it — see [Defaults and limits](/limits).

### Typing into it

It is the CLI itself: answer its questions, interrupt with `⌃C`, use its slash commands. Select text and press `⌘C` to copy.

Drop or paste a file onto it and the file is copied into the sandbox, with its path typed in as an `@` mention.

### What the agent is doing

The agent's cell in the surfaces column, and the session's row in the navigator, show a square mark. Hover the agent's cell for its word:

| Word | Mark | Meaning |
|---|---|---|
| **Starting** | Full, pulsing | The sandbox is booting. |
| **Working** | Full, pulsing | The agent is in a turn. |
| **Waiting** | A bar | The agent asked you something. |
| **Idle** | Empty | The turn is over; your move. |
| **Failed** | A cross | The sandbox failed to boot or is gone. |

`⌃⇥` jumps to the next session waiting for you, in any workspace.

### The Workflow button

The **Workflow** button in the window bar opens the run panel (`⌥⌘W`), which shows the [workflow](/workflows) the session follows. `⌥W` opens its picker: **No workflow** sends your prompts as written, **Agent proposes** lets the agent plan the steps, and a workflow of yours runs with **Run**.

During a run, the button shows the step number and a mark for where the run stands. The panel lists every step, with **Change workflow** and **Restart workflow**.

A step in the session's conversation runs in this same terminal once the step before it reports, so the whole run reads in one scrollback. A step set to start a new conversation runs in a side agent of its own instead.

### Side agents

**New agent** (**+** at the end of the surfaces column) opens a second agent in the same sandbox, with its own conversation. Agents a workflow step or the session's agent starts also appear as tabs there.

End a side agent with `Delete` or `⌫` on its row, or **Delete agent** in its right-click menu. If it is in the middle of a turn, Airlock asks first; what it already wrote stays. More in [Side agents](/side-agents).

The **Agent** panel (`⌘J`) is different: it answers questions about the page you are on. See [The window](/the-window).
